import { isFirefox, KEYS } from "@excalidraw/common";
import { SHAPES } from "@excalidraw/element/shapes";

import { probablySupportsClipboardBlob } from "../../clipboard";
import { t } from "../../i18n";
import { getShortcutKey } from "../../shortcut";

import type { JSX } from "react";

const Section = (props: { title: string; children: React.ReactNode }) => (
  <>
    <h3>{props.title}</h3>
    <div className="HelpDialog__islands-container">{props.children}</div>
  </>
);

const ShortcutIsland = (props: {
  caption: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`HelpDialog__island ${props.className}`}>
    <h4 className="HelpDialog__island-title">{props.caption}</h4>
    <div className="HelpDialog__island-content">{props.children}</div>
  </div>
);

function* intersperse(as: JSX.Element[][], delim: string | null) {
  let first = true;
  for (const x of as) {
    if (!first) {
      yield delim;
    }
    first = false;
    yield x;
  }
}

const upperCaseSingleChars = (str: string) => {
  return str.replace(/\b[a-z]\b/, (c) => c.toUpperCase());
};

const Shortcut = ({
  label,
  shortcuts,
  isOr = true,
}: {
  label: string;
  shortcuts: string[];
  isOr?: boolean;
}) => {
  const splitShortcutKeys = shortcuts.map((shortcut) => {
    const keys = shortcut.endsWith("++")
      ? [...shortcut.slice(0, -2).split("+"), "+"]
      : shortcut.split("+");

    return keys.map((key) => (
      <ShortcutKey key={key}>{upperCaseSingleChars(key)}</ShortcutKey>
    ));
  });

  return (
    <div className="HelpDialog__shortcut">
      <div>{label}</div>
      <div className="HelpDialog__key-container">
        {[...intersperse(splitShortcutKeys, isOr ? t("helpDialog.or") : null)]}
      </div>
    </div>
  );
};

const ShortcutKey = (props: { children: React.ReactNode }) => (
  <kbd className="HelpDialog__key" {...props} />
);
export default function BasicHelpDialog() {
  return (
    <Section title={t("helpDialog.shortcuts")}>
      <ShortcutIsland
        className="HelpDialog__island--tools"
        caption={t("helpDialog.tools")}
      >
        <Shortcut label={t("toolBar.hand")} shortcuts={[KEYS.H]} />
        {SHAPES.filter(({ myocSimplifiedMode }) => myocSimplifiedMode).map(
          ({ value, key }) => (
            <Shortcut
              key={value}
              label={t(`toolBar.${value}`)}
              shortcuts={[...key]}
            />
          ),
        )}
        <Shortcut
          label={t("helpDialog.cropStart")}
          shortcuts={[t("helpDialog.doubleClick"), getShortcutKey("Enter")]}
          isOr={true}
        />
        <Shortcut
          label={t("helpDialog.cropFinish")}
          shortcuts={[getShortcutKey("Enter"), getShortcutKey("Escape")]}
          isOr={true}
        />
      </ShortcutIsland>
      <ShortcutIsland
        className="HelpDialog__island--view"
        caption={t("helpDialog.view")}
      >
        <Shortcut label={t("helpDialog.smartZoom")} shortcuts={[KEYS.F]} />
        <Shortcut
          label={t("labels.flipViewpointHorizontal")}
          shortcuts={[getShortcutKey("Shift+R")]}
        />
        <Shortcut
          label={t("labels.flipViewpointVertical")}
          shortcuts={[getShortcutKey("Alt+Shift+R")]}
        />
        <Shortcut
          label={t("helpDialog.dragToZoom")}
          shortcuts={[`${KEYS.Z}+${t("helpDialog.drag")}`]}
        />
        <Shortcut
          label={t("buttons.objectsSnapMode")}
          shortcuts={[getShortcutKey("Alt+S")]}
        />
      </ShortcutIsland>
      <ShortcutIsland
        className="HelpDialog__island--editor"
        caption={t("helpDialog.editor")}
      >
        <Shortcut
          label={t("labels.pasteAsPlaintext")}
          shortcuts={[getShortcutKey("CtrlOrCmd+Shift+V")]}
        />
        <Shortcut
          label={t("labels.multiSelect")}
          shortcuts={[getShortcutKey(`Shift+${t("helpDialog.click")}`)]}
        />
        <Shortcut
          label={t("helpDialog.deepSelect")}
          shortcuts={[getShortcutKey(`CtrlOrCmd+${t("helpDialog.click")}`)]}
        />
        <Shortcut
          label={t("helpDialog.deepBoxSelect")}
          shortcuts={[getShortcutKey(`CtrlOrCmd+${t("helpDialog.drag")}`)]}
        />
        {/* firefox supports clipboard API under a flag, so we'll
                    show users what they can do in the error message */}
        {(probablySupportsClipboardBlob || isFirefox) && (
          <Shortcut
            label={t("labels.copyAsPng")}
            shortcuts={[getShortcutKey("Shift+Alt+C")]}
          />
        )}
        <Shortcut
          label={t("helpDialog.toggleElementLock")}
          shortcuts={[getShortcutKey("CtrlOrCmd+Shift+L")]}
        />
        <Shortcut
          label={t("labels.group")}
          shortcuts={[getShortcutKey("CtrlOrCmd+G")]}
        />
        <Shortcut
          label={t("labels.ungroup")}
          shortcuts={[getShortcutKey("CtrlOrCmd+Shift+G")]}
        />
        <Shortcut
          label={t("labels.flipHorizontal")}
          shortcuts={[getShortcutKey("Shift+H")]}
        />
        <Shortcut
          label={t("labels.flipVertical")}
          shortcuts={[getShortcutKey("Shift+V")]}
        />
      </ShortcutIsland>
    </Section>
  );
}
